import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import {
  Table,
  TableWithOrder,
  TableStatus,
  OrderChannel,
  Order,
  OrderItem,
} from './types'

// ─── Types for backend views ─────────────────────────────────
export interface CashierTableView {
  table_id: string
  restaurant_id: string
  table_number: string
  table_name: string | null
  zone: string | null
  capacity: number | null
  is_active: boolean
  table_status: string
  open_orders_count: number
  open_items_count: number
  open_total: number
  minutes_open: number | null
  order_channel: string | null
  waiter_name: string | null
  current_order_id: string | null
  current_order_status: string | null
}

export interface TableOrderItemView {
  order_item_id: string
  order_id: string
  table_id: string
  // Rancho — "Arma tu plato" items are inserted with menu_item_id = NULL.
  // PayTableModal usa este campo para detectarlos y forzar al cajero a
  // ingresar el precio manual antes de cobrar.
  menu_item_id: string | null
  item_name: string
  quantity: number
  unit_price: number
  line_total: number
  station_code: string | null
  station_name: string | null
  item_status: string
  notes: string | null
  paid_at: string | null
  paid_in_payment_id: string | null
  is_shareable: boolean
  // Opciones seleccionadas del ítem (ej. tipo de natural, de qué es el
  // casado). Sólo para mostrar detalle en Cobrar/Facturar — additivo,
  // no participa en ningún cálculo de dinero.
  selected_options?: Array<{
    option_group?: string | null
    option_name?: string | null
    price_delta?: number | null
  }>
}

export interface OpenTableAccount {
  table_id: string
  table_number: string
  table_name: string | null
  zone: string | null
  order_id: string
  order_number: string
  customer_name: string | null
  order_status: string
  order_channel: string | null
  waiter_name: string | null
  open_total: number
  minutes_open: number
  items_count: number
  created_at: string
}

export interface ActionResult {
  success: boolean
  error?: string
}

// ─── Main Table Management Hook ──────────────────────────────
export function useTableManagement(restaurantId: string | undefined) {
  const [tables, setTables] = useState<TableWithOrder[]>([])
  const [loading, setLoading] = useState(true)

  const mapViewStatus = (viewStatus: string): TableStatus => {
    switch (viewStatus?.toLowerCase()) {
      case 'free':
      case 'available':
        return 'free'
      case 'occupied':
        return 'occupied'
      case 'ordering':
        return 'ordering'
      case 'waiting_payment':
        return 'waiting_payment'
      case 'closed':
        return 'closed'
      default:
        return 'free'
    }
  }

  const mapOrderChannel = (channel: string | null): OrderChannel | null => {
    if (!channel) return null
    const ch = channel.toLowerCase()
    if (ch === 'qr' || ch.includes('self')) return 'qr'
    if (ch === 'waiter' || ch.includes('mesero')) return 'waiter'
    if (ch === 'cashier' || ch.includes('caja')) return 'cashier'
    if (ch === 'web') return 'web'
    return 'web'
  }

  const computeTableStatus = (order: Order | null): TableStatus => {
    if (!order) return 'free'
    switch (order.status) {
      case 'new':
        return 'ordering'
      case 'accepted':
      case 'preparing':
        return 'occupied'
      case 'ready':
        return 'waiting_payment'
      case 'delivered':
      case 'cancelled':
        return 'closed'
      default:
        return 'occupied'
    }
  }

  const getOrderChannel = (order: Order | null): OrderChannel | null => {
    if (!order) return null
    const src = order.source?.toLowerCase() || ''
    if (src.includes('qr') || src.includes('self')) return 'qr'
    if (src.includes('waiter') || src.includes('mesero')) return 'waiter'
    if (src.includes('cashier') || src.includes('caja')) return 'cashier'
    if (src.includes('web') || src.includes('online')) return 'web'
    if (order.table_id && order.delivery_type === 'dine_in') return 'qr'
    return 'web'
  }

  const fetchTablesFromView = useCallback(async (): Promise<boolean> => {
    if (!restaurantId) return false
    try {
      const { data, error } = await supabase
        .from('v_restaurant_tables_cashier')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('table_number')

      if (error) throw error
      if (!data) return false

      const mapped: TableWithOrder[] = data.map((row: any) => ({
        id: row.table_id,
        restaurant_id: row.restaurant_id,
        table_number: row.table_number,
        table_name: row.table_name,
        zone: row.zone,
        capacity: row.capacity,
        qr_code_url: null,
        qr_token: null,
        is_active: row.is_active ?? true,
        created_at: '',
        updated_at: '',
        current_order: row.current_order_id
          ? ({
              id: row.current_order_id,
              order_number: row.order_number || '',
              status: row.current_order_status || 'new',
              total: row.open_total || 0,
              customer_name: row.customer_name || '',
              created_at: row.order_created_at || '',
            } as any)
          : null,
        current_order_items: [],
        status: mapViewStatus(row.table_status),
        order_channel: mapOrderChannel(row.order_channel),
        time_open_minutes: row.minutes_open ?? null,
        item_count: row.open_items_count || 0,
        waiter_name: row.waiter_name || null,
      }))

      setTables(mapped)
      return true
    } catch {
      return false
    }
  }, [restaurantId])

  const fetchTablesFallback = useCallback(async () => {
    if (!restaurantId) return
    try {
      const { data: tablesData, error: tablesError } = await supabase
        .from('tables')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('is_active', true)
        .order('table_number')

      if (tablesError) throw tablesError
      if (!tablesData || tablesData.length === 0) {
        setTables([])
        return
      }

      const { data: ordersData } = await supabase
        .from('orders')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .not('table_id', 'is', null)
        .not('status', 'in', '("delivered","cancelled")')
        .order('created_at', { ascending: false })

      const activeOrders = ordersData || []
      const orderIds = activeOrders.map((o) => o.id)
      let allItems: OrderItem[] = []
      if (orderIds.length > 0) {
        const { data: itemsData } = await supabase
          .from('order_items')
          .select('*')
          .in('order_id', orderIds)

        allItems = itemsData || []
      }

      const tablesWithOrders: TableWithOrder[] = tablesData.map(
        (table: Table) => {
          const currentOrder =
            activeOrders.find((o) => o.table_id === table.id) || null
          const orderItems = currentOrder
            ? allItems.filter((i) => i.order_id === currentOrder.id)
            : []
          const timeOpen = currentOrder
            ? Math.floor(
                (new Date().getTime() -
                  new Date(currentOrder.created_at).getTime()) /
                  60000,
              )
            : null

          return {
            ...table,
            current_order: currentOrder,
            current_order_items: orderItems,
            status: computeTableStatus(currentOrder),
            order_channel: getOrderChannel(currentOrder),
            time_open_minutes: timeOpen,
            item_count: orderItems.reduce((sum, i) => sum + i.quantity, 0),
          }
        },
      )

      setTables(tablesWithOrders)
    } catch (error) {
      console.error('[NOIZA] Fallback table fetch error:', error)
    }
  }, [restaurantId])

  const fetchTables = useCallback(async () => {
    if (!restaurantId) return
    try {
      const viewWorked = await fetchTablesFromView()
      if (!viewWorked) {
        await fetchTablesFallback()
      }
    } catch {
      await fetchTablesFallback()
    } finally {
      setLoading(false)
    }
  }, [restaurantId, fetchTablesFromView, fetchTablesFallback])

  useEffect(() => {
    fetchTables()
    const interval = setInterval(fetchTables, 8000)
    return () => clearInterval(interval)
  }, [fetchTables])

  return { tables, loading, refetch: fetchTables }
}

// ─── Table Order Items Hook ──────────────────────────────────
// NOTE: Optional `orderId` is an additive fallback used for marketplace
// orders that have no associated table (e.g. La Cabaña Parrillera taking
// delivery/pickup orders through caja). When `tableId` is missing but
// `orderId` is provided, we load the items directly by order id so the
// PayTableModal can still drive the cobrar/facturar flow.
export function useTableOrderItems(
  tableId: string | undefined,
  restaurantId: string | undefined,
  orderId?: string | undefined,
) {
  const [items, setItems] = useState<TableOrderItemView[]>([])
  const [loading, setLoading] = useState(false)

  const fetchItems = useCallback(async () => {
    if (!restaurantId) return
    if (!tableId && !orderId) return
    setLoading(true)

    // ─── Cancelled-status filter ──────────────────────────────────────
    // Items that have been removed/cancelled from the order (e.g. via the
    // "Modificar / Cancelar" modal) keep their row for historical display
    // (shown under "QUITADOS DE LA ORDEN"), but they MUST NOT participate
    // in any active money math: occupied-table total, Cobrar Mesa total,
    // split-bill, remaining-by-cobrar. The view v_table_order_items only
    // filters by ORDER status, not by ITEM status, so we filter here at
    // the single source-of-truth hook so every downstream consumer is
    // automatically consistent (TableManagementPage, PayTableModal, etc.).
    const CANCELLED_ITEM_STATUSES = new Set([
      'cancelled',
      'canceled',
      'removed',
      'voided',
      'deleted',
    ])
    const isCancelledStatus = (s: any) =>
      CANCELLED_ITEM_STATUSES.has(
        String(s || '')
          .toLowerCase()
          .trim(),
      )

    // ── Path A: no table → load items by order_id directly. Used for
    //    marketplace orders (delivery / pickup) that need Cobrar/Facturar.
    if (!tableId && orderId) {
      try {
        const { data: itemsData } = await supabase
          .from('order_items')
          .select('*')
          .eq('order_id', orderId)

        // Enriquecimiento visual (no-bloqueante): opciones por ítem para
        // mostrar el detalle (tipo de natural, de qué es el casado) en
        // Cobrar/Facturar. Si falla, seguimos sin opciones.
        const optsByItemA: Record<string, any[]> = {}
        try {
          const idsA = (itemsData || []).map((i: any) => i.id)
          if (idsA.length > 0) {
            const { data: optRowsA } = await supabase
              .from('order_item_options')
              .select('order_item_id, option_group, option_name, price_delta')
              .in('order_item_id', idsA)
            ;(optRowsA || []).forEach((o: any) => {
              if (!optsByItemA[o.order_item_id])
                optsByItemA[o.order_item_id] = []
              optsByItemA[o.order_item_id].push({
                option_group: o.option_group,
                option_name: o.option_name,
                price_delta: o.price_delta,
              })
            })
          }
        } catch {
          // opciones son sólo decorativas; ignoramos errores
        }

        setItems(
          (itemsData || [])
            .filter((item) => !isCancelledStatus(item.status))
            .map((item) => ({
              order_item_id: item.id,
              order_id: item.order_id,
              table_id: '',
              menu_item_id: item.menu_item_id ?? null,
              item_name: item.item_name_snapshot,
              quantity: item.quantity,
              unit_price: item.unit_price_snapshot,
              line_total: item.line_total,
              station_code: item.station_code,
              station_name: item.station_code,
              item_status: item.status,
              notes: item.notes,
              paid_at: item.paid_at || null,
              paid_in_payment_id: item.paid_in_payment_id || null,
              is_shareable: item.is_shareable_snapshot || false,
              selected_options: optsByItemA[item.id] || [],
            })),
        )
      } catch (err) {
        console.error('[NOIZA] Error fetching order items by id:', err)
        setItems([])
      } finally {
        setLoading(false)
      }
      return
    }

    // ─── Path B (HARDENED) — table-bound items ────────────────────────
    // SAFETY HARDENING (2026-05-31): Previously this path used
    // v_table_order_items as the primary data source and only fell back
    // to order_items if the view returned an empty array. That was
    // fragile — any view filter or schema mismatch could silently drop
    // an item that was actually persisted (e.g. NatuCafé Estantería /
    // retail items appearing in order_items but missing from the view).
    //
    // New behaviour: order_items IS THE SOURCE OF TRUTH. We:
    //   1. Find all in-flight orders for the table from `orders`.
    //   2. Read ALL their order_items directly (the bytes that exist).
    //   3. Try to enrich each row with station_name from the view —
    //      but only as a non-blocking nice-to-have. If the view drops
    //      a row, we keep the order_items row anyway.
    //
    // Result: any row in order_items linked to an active table order
    // will appear in Cobrar Mesa, period. No silent drops.
    try {
      const { data: orders, error: ordersErr } = await supabase
        .from('orders')
        .select('id')
        .eq('restaurant_id', restaurantId)
        .eq('table_id', tableId)
        .not('status', 'in', '("delivered","cancelled")')

      if (ordersErr) throw ordersErr

      if (!orders || orders.length === 0) {
        setItems([])
        setLoading(false)
        return
      }

      const orderIds = orders.map((o: any) => o.id)

      // 1) Source of truth — direct order_items read.
      const { data: rawItems, error: itemsErr } = await supabase
        .from('order_items')
        .select('*')
        .in('order_id', orderIds)

      if (itemsErr) throw itemsErr

      // 2) Optional enrichment — pull station_name + any view-only
      //    columns. Non-blocking: if the view fails or filters items,
      //    we still keep the order_items rows.
      let viewMap = new Map<string, any>()
      try {
        const { data: viewRows } = await supabase
          .from('v_table_order_items')
          .select('*')
          .eq('table_id', tableId)
        if (viewRows && viewRows.length > 0) {
          viewMap = new Map(
            viewRows.map((r: any) => [String(r.order_item_id || r.id), r]),
          )
        }
      } catch {
        // View is optional. Carry on with order_items only.
      }

      // Defensive logging: if the view dropped any rows, surface it so
      // we can spot future schema regressions early.
      const droppedByView = (rawItems || []).filter(
        (it: any) => !viewMap.has(String(it.id)),
      )
      if (viewMap.size > 0 && droppedByView.length > 0) {
        console.warn(
          '[NOIZA] v_table_order_items dropped',
          droppedByView.length,
          'row(s) for table',
          tableId,
          '— using order_items as source of truth. Item ids:',
          droppedByView.map((it: any) => it.id),
        )
      }

      // Enriquecimiento visual (no-bloqueante): opciones por ítem para
      // mostrar el detalle (tipo de natural, de qué es el casado) en
      // Cobrar/Facturar. Si falla, seguimos sin opciones.
      const optsByItemB: Record<string, any[]> = {}
      try {
        const idsB = (rawItems || []).map((i: any) => i.id)
        if (idsB.length > 0) {
          const { data: optRowsB } = await supabase
            .from('order_item_options')
            .select('order_item_id, option_group, option_name, price_delta')
            .in('order_item_id', idsB)
          ;(optRowsB || []).forEach((o: any) => {
            if (!optsByItemB[o.order_item_id]) optsByItemB[o.order_item_id] = []
            optsByItemB[o.order_item_id].push({
              option_group: o.option_group,
              option_name: o.option_name,
              price_delta: o.price_delta,
            })
          })
        }
      } catch {
        // opciones son sólo decorativas; ignoramos errores
      }

      const mapped: TableOrderItemView[] = (rawItems || [])
        .filter((item: any) => !isCancelledStatus(item.status))
        .map((item: any) => {
          const viewRow = viewMap.get(String(item.id))
          return {
            order_item_id: item.id,
            order_id: item.order_id,
            table_id: tableId as string,
            menu_item_id: item.menu_item_id ?? null,
            item_name: item.item_name_snapshot,
            quantity: item.quantity,
            unit_price: item.unit_price_snapshot,
            line_total: item.line_total,
            station_code: item.station_code,
            // Prefer the view's station_name (kitchen_stations.name)
            // when available; fall back to the raw station_code.
            station_name: viewRow?.station_name || item.station_code || null,
            item_status: item.status,
            notes: item.notes,
            paid_at: item.paid_at || null,
            paid_in_payment_id: item.paid_in_payment_id || null,
            is_shareable: item.is_shareable_snapshot || false,
            selected_options: optsByItemB[item.id] || [],
          }
        })

      setItems(mapped)
    } catch (err) {
      console.error('[NOIZA] Error fetching table items:', err)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [tableId, restaurantId, orderId])

  useEffect(() => {
    fetchItems()
  }, [fetchItems])

  return { items, loading, refetch: fetchItems }
}

// ─── Close Table Account ─────────────────────────────────────
export async function closeTableAccount(
  restaurantId: string,
  tableId: string,
  paymentMethod: string,
  closedByUserId: string | null,
): Promise<ActionResult> {
  try {
    const { error } = await supabase.rpc('close_table_account', {
      p_restaurant_id: restaurantId,
      p_table_id: tableId,
      p_payment_method: paymentMethod,
      p_closed_by_user_id: closedByUserId || null,
    })

    if (error) {
      // If RPC doesn't exist, fallback to manual close
      if (error.message?.includes('does not exist') || error.code === '42883') {
        return await closeTableAccountFallback(
          restaurantId,
          tableId,
          paymentMethod,
        )
      }
      return { success: false, error: error.message }
    }

    return { success: true }
  } catch (err: any) {
    return { success: false, error: err?.message || 'Error al cerrar cuenta' }
  }
}

async function closeTableAccountFallback(
  restaurantId: string,
  tableId: string,
  paymentMethod: string,
): Promise<ActionResult> {
  try {
    // Find active orders for this table
    const { data: orders, error: fetchErr } = await supabase
      .from('orders')
      .select('id, status')
      .eq('restaurant_id', restaurantId)
      .eq('table_id', tableId)
      .not('status', 'in', '("delivered","cancelled")')

    if (fetchErr) throw fetchErr
    if (!orders || orders.length === 0) {
      return { success: false, error: 'No hay cuenta abierta en esta mesa' }
    }

    // Mark all active orders as delivered
    for (const order of orders) {
      const { error: updateErr } = await supabase
        .from('orders')
        .update({
          status: 'delivered',
          payment_method: paymentMethod,
          updated_at: new Date().toISOString(),
        })
        .eq('id', order.id)

      if (updateErr) {
        console.error('[NOIZA] Error closing order:', updateErr)
      }
    }

    return { success: true }
  } catch (err: any) {
    return { success: false, error: err?.message || 'Error al cerrar cuenta' }
  }
}

// ─── Table Metrics Hook ──────────────────────────────────────
export interface TableMetrics {
  qrOrdersToday: number
  waiterOrdersToday: number
  selfServicePercent: number
  waiterPercent: number
  activeTables: number
  totalTables: number
  averageTicketPerTable: number
  totalRevenueToday: number
}

export function useTableMetrics(restaurantId: string | undefined) {
  const [metrics, setMetrics] = useState<TableMetrics>({
    qrOrdersToday: 0,
    waiterOrdersToday: 0,
    selfServicePercent: 0,
    waiterPercent: 0,
    activeTables: 0,
    totalTables: 0,
    averageTicketPerTable: 0,
    totalRevenueToday: 0,
  })
  const [loading, setLoading] = useState(true)

  const fetchMetricsFromViews = useCallback(async (): Promise<boolean> => {
    if (!restaurantId) return false
    try {
      const [channelRes, pctRes, activeRes, avgRes] = await Promise.all([
        supabase
          .from('v_service_channel_today')
          .select('*')
          .eq('restaurant_id', restaurantId),
        supabase
          .from('v_service_channel_today_pct')
          .select('*')
          .eq('restaurant_id', restaurantId),
        supabase
          .from('v_active_tables_today')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .single(),
        supabase
          .from('v_avg_ticket_per_table_today')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .single(),
      ])

      // If any critical view fails, fall back
      if (channelRes.error && activeRes.error) return false

      let qr = 0,
        waiter = 0,
        qrPct = 0,
        waiterPct = 0
      const channels = channelRes.data || []
      channels.forEach((row: any) => {
        const ch = (row.order_channel || row.channel || '').toLowerCase()
        if (ch === 'qr') qr = row.order_count || row.orders_count || 0
        if (ch === 'waiter' || ch === 'mesero')
          waiter = row.order_count || row.orders_count || 0
      })

      const pcts = pctRes.data || []
      pcts.forEach((row: any) => {
        const ch = (row.order_channel || row.channel || '').toLowerCase()
        if (ch === 'qr') qrPct = Math.round(row.pct || row.percentage || 0)
        if (ch === 'waiter' || ch === 'mesero')
          waiterPct = Math.round(row.pct || row.percentage || 0)
      })

      const activeTables =
        activeRes.data?.active_tables || activeRes.data?.count || 0
      const totalTables = activeRes.data?.total_tables || 0
      const avgTicket =
        avgRes.data?.avg_ticket || avgRes.data?.average_ticket || 0

      setMetrics({
        qrOrdersToday: qr,
        waiterOrdersToday: waiter,
        selfServicePercent: qrPct,
        waiterPercent: waiterPct,
        activeTables,
        totalTables,
        averageTicketPerTable: Math.round(avgTicket),
        totalRevenueToday: 0,
      })

      return true
    } catch {
      return false
    }
  }, [restaurantId])

  const fetchMetricsFallback = useCallback(async () => {
    if (!restaurantId) return
    try {
      const todayStart = new Date()
      todayStart.setHours(0, 0, 0, 0)

      const [ordersRes, tablesRes, activeRes] = await Promise.all([
        supabase
          .from('orders')
          .select(
            'id, restaurant_id, status, total, table_id, delivery_type, source, created_at',
          )
          .eq('restaurant_id', restaurantId)
          .gte('created_at', todayStart.toISOString())
          .not('status', 'eq', 'cancelled'),
        supabase
          .from('tables')
          .select('id')
          .eq('restaurant_id', restaurantId)
          .eq('is_active', true),
        supabase
          .from('orders')
          .select('table_id')
          .eq('restaurant_id', restaurantId)
          .not('table_id', 'is', null)
          .not('status', 'in', '("delivered","cancelled")'),
      ])

      const orders = ordersRes.data || []
      const totalTables = tablesRes.data?.length || 0
      const activeTableIds = new Set(
        (activeRes.data || []).map((o: any) => o.table_id).filter(Boolean),
      )

      let qrCount = 0,
        waiterCount = 0,
        tableRevenue = 0
      orders.forEach((order: any) => {
        const src = (order.source || '').toLowerCase()
        const isDineIn = order.delivery_type === 'dine_in' || order.table_id
        if (!isDineIn) return

        if (
          src.includes('qr') ||
          src.includes('self') ||
          (order.table_id &&
            !src.includes('waiter') &&
            !src.includes('mesero') &&
            !src.includes('caja'))
        ) {
          qrCount++
        } else if (src.includes('waiter') || src.includes('mesero')) {
          waiterCount++
        } else if (order.table_id) {
          qrCount++
        }
        if (order.table_id) tableRevenue += order.total || 0
      })

      const totalDineIn = qrCount + waiterCount
      const dineInWithTable = orders.filter((o: any) => o.table_id).length

      setMetrics({
        qrOrdersToday: qrCount,
        waiterOrdersToday: waiterCount,
        selfServicePercent:
          totalDineIn > 0 ? Math.round((qrCount / totalDineIn) * 100) : 0,
        waiterPercent:
          totalDineIn > 0 ? Math.round((waiterCount / totalDineIn) * 100) : 0,
        activeTables: activeTableIds.size,
        totalTables,
        averageTicketPerTable:
          dineInWithTable > 0 ? Math.round(tableRevenue / dineInWithTable) : 0,
        totalRevenueToday: tableRevenue,
      })
    } catch (error) {
      console.error('[NOIZA] Fallback metrics error:', error)
    }
  }, [restaurantId])

  useEffect(() => {
    if (!restaurantId) return

    async function fetchMetrics() {
      setLoading(true)
      try {
        const viewWorked = await fetchMetricsFromViews()
        if (!viewWorked) await fetchMetricsFallback()
      } catch {
        await fetchMetricsFallback()
      } finally {
        setLoading(false)
      }
    }

    fetchMetrics()
    const interval = setInterval(fetchMetrics, 60000)
    return () => clearInterval(interval)
  }, [restaurantId, fetchMetricsFromViews, fetchMetricsFallback])

  return { metrics, loading }
}
